// Табличка-перевертень: под углом 45° слева читается одно слово, справа — другое.
// Каждый блок — пересечение буквы первого слова, выдавленной вдоль взгляда справа, и буквы второго — вдоль взгляда слева.
// Если букв не поровну, буква короткого слова удлиняется вглубь и вмещает две (или больше) буквы длинного.
// Фразы с пробелами: слова идут парами (первое с первым…), между парами — промежуток WS.
// Блоки и подставка — две части одного объекта (3MF): им можно дать разный пластик.
// На длинных сторонах подставки — надписи спереди и сзади (выпуклые или врезанные), третьей частью.
import { bedPicker, bedWarn, createStage, fitsBed, glyphShapes, loadFont, scope, solid, textContours, toGeometry, type Ctx, type Model, type ViewPart } from '@gen3d/core';
import type { CrossSection, Manifold } from 'manifold-3d/manifold';

type Base = 'round' | 'rect' | 'none';
type S = {
  a: string; b: string; font: string; keep: boolean;
  W: number; H: number; G: number; G2: number; WS: number; SK: number;
  base: Base; T: number; M: number;
  /** Надписи на подставке: спереди, сзади, шрифт, рельеф, высота заглавных и глубина/высота рельефа. */
  front: string; back: string; sfont: string; smode: 'raised' | 'engraved'; SS: number; SD: number;
  /** Ширина надписи на подставке, % (растяжение по стороне, высота та же). */
  SW: number; BW: number;
  /** Масштаб всей модели, %. */
  SC: number;
  /** По парам слов: сколько букв длинного слова на каждой букве короткого; null — расставить поровну. */
  split: number[][] | null;
};
type NumKey = 'W' | 'H' | 'G' | 'G2' | 'WS' | 'SK' | 'T' | 'M' | 'SS' | 'SD' | 'SC' | 'SW' | 'BW';

// Шрифты из /a/fonts/: проверены на украинские буквы. Oswald — узкий жирный, ближе всего к ручным заготовкам.
const FONTS: [id: string, name: string][] = [
  ['oswald-600', 'Oswald'], ['roboto-700', 'Roboto'], ['pt-sans-700', 'PT Sans'], ['montserrat-700', 'Montserrat'], ['russo-one-400', 'Russo One'],
  ['rubik-700', 'Rubik'], ['manrope-800', 'Manrope'], ['nunito-800', 'Nunito'], ['ubuntu-700', 'Ubuntu'], ['exo-2-700', 'Exo 2'],
  ['unbounded-700', 'Unbounded'], ['rubik-mono-one-400', 'Rubik Mono One'], ['roboto-slab-700', 'Roboto Slab'], ['bitter-700', 'Bitter'],
];

// для надписи на подставке — все шрифты генератора таблички
const SIDE_FONTS: [group: 'sans' | 'serif' | 'hand' | 'deco', id: string, name: string][] = [
  ['sans', 'rubik-700', 'Rubik'], ['sans', 'manrope-800', 'Manrope'], ['sans', 'montserrat-700', 'Montserrat'],
  ['sans', 'roboto-700', 'Roboto'], ['sans', 'ibm-plex-sans-700', 'IBM Plex Sans'], ['sans', 'pt-sans-700', 'PT Sans'],
  ['sans', 'nunito-800', 'Nunito'], ['sans', 'ubuntu-700', 'Ubuntu'], ['sans', 'exo-2-700', 'Exo 2'],
  ['sans', 'oswald-600', 'Oswald'], ['sans', 'comfortaa-700', 'Comfortaa'],
  ['serif', 'roboto-slab-700', 'Roboto Slab'], ['serif', 'bitter-700', 'Bitter'],
  ['serif', 'playfair-display-700', 'Playfair Display'], ['serif', 'yeseva-one-400', 'Yeseva One'],
  ['hand', 'caveat-700', 'Caveat'], ['hand', 'marck-script-400', 'Marck Script'], ['hand', 'neucha-400', 'Neucha'],
  ['hand', 'pacifico-400', 'Pacifico'], ['hand', 'lobster-400', 'Lobster'],
  ['deco', 'unbounded-700', 'Unbounded'], ['deco', 'russo-one-400', 'Russo One'], ['deco', 'rubik-mono-one-400', 'Rubik Mono One'],
  ['deco', 'ibm-plex-mono-600', 'IBM Plex Mono'], ['deco', 'press-start-2p-400', 'Press Start 2P'],
];

const T = {
  uk: {
    words: 'Слова', a: 'Видно зліва', b: 'Видно справа', font: 'Шрифт',
    BW: 'Жирність', boldHint: '0 — як у шрифті. Більше — контур літер товщий, тонкі місця міцніші; дрібні отвори в літерах можуть затягнутися.',
    keep: 'Зберегти пропорції літер', keepHint: 'Без галочки кожна літера розтягнута на весь блок — обидва слова читаються однаково щільно.',
    size: 'Розмір', W: 'Ширина літери', H: 'Висота літер', G: 'Відстань між блоками', G2: 'Між літерами в подвійному блоці', SC: 'Масштаб усієї моделі', scHint: 'Зменшує або збільшує все разом: літери, підставку, напис. Розміри вище — для 100 %.', WS: 'Між словами', SK: 'Втопити літери', skHint: 'Низ літер заходить у підставку: круглі літери (О, С) стоять на площадці, а не на ребрі.',
    split: 'Подвійні літери', splitHint: 'Літер не порівну: на {n} з цих літер стане по дві літери іншого слова. Натисніть, щоб вибрати.',
    base: 'Підставка', round: 'Заокруглена', rect: 'Прямокутна', none: 'Без підставки', T: 'Товщина', M: 'Відступ від літер',
    side: 'Напис на підставці', front: 'Спереду', back: 'Ззаду', raised: 'Опуклий', engraved: 'Врізаний', SS: 'Висота великих літер', SW: 'Ширина напису', SDr: 'Висота рельєфу', SDe: 'Глибина', groups: { sans: 'Без засічок', serif: 'Із засічками', hand: 'Рукописні', deco: 'Декоративні' },
    sideLong: 'Напис на підставці довший за її рівну сторону: {w}', sideTall: 'Напис вищий за підставку — зменште літери',
    view: { a: 'Зліва', b: 'Справа', top: '3D' },
    mm: 'мм', min: 'мін.', max: 'макс.',
    ok: 'Готово', loading: 'Будую модель…', fontErr: 'Не вдалося завантажити шрифт',
    empty: 'Впишіть обидва слова', tooMany: 'В одному слові більш ніж утричі більше літер — скоротіть довше слово',
    loose: 'Відірвані шматочки в блоках: {l} — вони не триматимуться. Спробуйте інший шрифт або інші пари літер.',
    thin: 'Тонкі місця в блоках: {l}', turned: 'Повернуто на столі на {d}°, щоб вмістилася.', spaces: 'Пробіли не враховано: у фразах різна кількість слів ({a} і {b}).', noGlyph: 'Немає в шрифті — пропущено: {c}',
    defA: 'EPIC', defB: '3D',
  },
  ru: {
    words: 'Слова', a: 'Видно слева', b: 'Видно справа', font: 'Шрифт',
    BW: 'Жирность', boldHint: '0 — как в шрифте. Больше — контур букв толще, тонкие места прочнее; мелкие отверстия в буквах могут затянуться.',
    keep: 'Сохранить пропорции букв', keepHint: 'Без галочки каждая буква растянута на весь блок — оба слова читаются одинаково плотно.',
    size: 'Размер', W: 'Ширина буквы', H: 'Высота букв', G: 'Расстояние между блоками', G2: 'Между буквами в двойном блоке', SC: 'Масштаб всей модели', scHint: 'Уменьшает или увеличивает всё вместе: буквы, подставку, надпись. Размеры выше — для 100 %.', WS: 'Между словами', SK: 'Утопить буквы', skHint: 'Низ букв уходит в подставку: круглые буквы (О, С) стоят на площадке, а не на ребре.',
    split: 'Двойные буквы', splitHint: 'Букв не поровну: на {n} из этих букв встанет по две буквы другого слова. Нажмите, чтобы выбрать.',
    base: 'Подставка', round: 'Скруглённая', rect: 'Прямоугольная', none: 'Без подставки', T: 'Толщина', M: 'Отступ от букв',
    side: 'Надпись на подставке', front: 'Спереди', back: 'Сзади', raised: 'Выпуклая', engraved: 'Врезанная', SS: 'Высота заглавных букв', SW: 'Ширина надписи', SDr: 'Высота рельефа', SDe: 'Глубина', groups: { sans: 'Без засечек', serif: 'С засечками', hand: 'Рукописные', deco: 'Декоративные' },
    sideLong: 'Надпись на подставке длиннее её ровной стороны: {w}', sideTall: 'Надпись выше подставки — уменьшите буквы',
    view: { a: 'Слева', b: 'Справа', top: '3D' },
    mm: 'мм', min: 'мин.', max: 'макс.',
    ok: 'Готово', loading: 'Строю модель…', fontErr: 'Не удалось загрузить шрифт',
    empty: 'Впишите оба слова', tooMany: 'В одном слове больше чем втрое больше букв — сократите длинное слово',
    loose: 'Оторванные кусочки в блоках: {l} — они не будут держаться. Попробуйте другой шрифт или другие пары букв.',
    thin: 'Тонкие места в блоках: {l}', turned: 'Развёрнута на столе на {d}°, чтобы поместилась.', spaces: 'Пробелы не учтены: во фразах разное число слов ({a} и {b}).', noGlyph: 'Нет в шрифте — пропущено: {c}',
    defA: 'EPIC', defB: '3D',
  },
  en: {
    words: 'Words', a: 'Seen from the left', b: 'Seen from the right', font: 'Font',
    BW: 'Boldness', boldHint: '0 is the font as is. Higher thickens the letter outlines so thin spots get sturdier; small holes in letters may close up.',
    keep: 'Keep letter proportions', keepHint: 'Unchecked, every letter is stretched to fill its block, so both words read equally solid.',
    size: 'Size', W: 'Letter width', H: 'Letter height', G: 'Gap between blocks', G2: 'Gap inside a double block', SC: 'Scale of the whole model', scHint: 'Shrinks or enlarges everything together: letters, stand, text. The sizes above are for 100%.', WS: 'Gap between words', SK: 'Sink letters', skHint: 'The bottom of the letters goes into the stand, so round letters (O, C) stand on a flat patch rather than an edge.',
    split: 'Double letters', splitHint: 'The words differ in length: {n} of these letters will each carry two letters of the other word. Tap to choose.',
    base: 'Stand', round: 'Rounded', rect: 'Rectangular', none: 'No stand', T: 'Thickness', M: 'Margin around letters',
    side: 'Text on the stand', front: 'Front', back: 'Back', raised: 'Raised', engraved: 'Engraved', SS: 'Capital letter height', SW: 'Text width', SDr: 'Relief height', SDe: 'Depth', groups: { sans: 'Sans serif', serif: 'Serif', hand: 'Handwritten', deco: 'Display' },
    sideLong: 'The stand text is longer than its flat side: {w}', sideTall: 'The stand text is taller than the stand — make it smaller',
    view: { a: 'Left', b: 'Right', top: '3D' },
    mm: 'mm', min: 'min', max: 'max',
    ok: 'Ready', loading: 'Building model…', fontErr: 'Could not load the font',
    empty: 'Enter both words', tooMany: 'One word has more than three times as many letters — shorten the longer one',
    loose: 'Loose bits in blocks: {l} — they will not hold. Try another font or other letter pairs.',
    thin: 'Thin spots in blocks: {l}', turned: 'Turned {d}° on the bed to fit.', spaces: 'Spaces ignored: the phrases have different numbers of words ({a} and {b}).', noGlyph: 'Not in the font, skipped: {c}',
    defA: 'EPIC', defB: '3D',
  },
};

// [мин, макс, шаг]
const LIM: Record<NumKey, [number, number, number]> = {
  W: [8, 60, 0.5], H: [8, 120, 0.5], G: [0, 10, 0.1], G2: [0, 10, 0.1], WS: [0, 40, 0.5], SK: [0, 5, 0.1], SS: [3, 30, 0.5], SD: [0.4, 3, 0.1], SC: [20, 200, 1], SW: [50, 300, 5], BW: [0, 12, 1], T: [1, 30, 0.5], M: [0, 30, 0.5],
};
const MAXK = 3; // букв длинного слова на одной букве короткого
const MAXLEN = 30;
/** Буквы, которые всегда в своих пропорциях, даже без «Сохранить пропорции». */
const NARROW = new Set(['І', 'Ї', 'I']);

const clamp = (v: number, [a, b]: [number, number, number]) => Math.min(b, Math.max(a, v));
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
/** Слова фразы: заглавными, по пробелам. */
const words = (s: string) => s.normalize('NFC').toUpperCase().split(/\s+/).filter(Boolean).map((w) => [...w]);

/** Поровну: n букв короткого слова, m — длинного; лишние раздаются через равные промежутки. */
function evenSplit(n: number, m: number): number[] {
  // e-я лишняя буква — на букву около доли (e + 0.5) / extra короткого слова
  const out = Array<number>(n).fill(1), extra = m - n;
  for (let e = 0; e < extra; e++) out[Math.min(n - 1, Math.floor(((e + 0.5) * n) / extra))] += 1;
  return out;
}

type Store = { load(): unknown; save(v: unknown): void };

export function mount(root: HTMLElement, { lang, store }: Ctx & { store?: Store }) {
  const t = T[lang] ?? T.uk;
  const s: S = { a: t.defA, b: t.defB, font: 'oswald-600', keep: false, W: 15, H: 30, G: 1.5, G2: 2, WS: 10, SK: 1, base: 'round', T: 15, M: 10, front: '', back: '', sfont: 'rubik-700', smode: 'raised', SS: 7, SD: 1, SW: 100, BW: 0, SC: 100, split: null };
  try {
    const v = (store?.load() as { s?: Record<string, any> } | null)?.s;
    if (v && typeof v === 'object') {
      for (const k of Object.keys(LIM) as NumKey[]) if (typeof v[k] === 'number' && isFinite(v[k])) s[k] = clamp(v[k], LIM[k]);
      if (typeof v.a === 'string') s.a = v.a.slice(0, MAXLEN);
      if (typeof v.b === 'string') s.b = v.b.slice(0, MAXLEN);
      if (FONTS.some(([id]) => id === v.font)) s.font = v.font;
      if (typeof v.keep === 'boolean') s.keep = v.keep;
      if (v.bold === true && typeof v.BW !== 'number') s.BW = 4; // ссылки из v14 с галочкой «Жирнее»
      if (['round', 'rect', 'none'].includes(v.base)) s.base = v.base;
      if (typeof v.front === 'string') s.front = v.front.slice(0, 60);
      if (typeof v.back === 'string') s.back = v.back.slice(0, 60);
      if (SIDE_FONTS.some(([, id]) => id === v.sfont)) s.sfont = v.sfont;
      if (v.smode === 'raised' || v.smode === 'engraved') s.smode = v.smode;
      const okRow = (r: unknown) => Array.isArray(r) && r.every((x) => Number.isInteger(x) && x >= 1 && x <= MAXK);
      if (Array.isArray(v.split) && v.split.every(okRow)) s.split = v.split;
    }
  } catch { /* типовые */ }

  const stage = root.querySelector<HTMLElement>('[data-stage]')!;
  const panel = root.querySelector<HTMLElement>('[data-params]')!;
  stage.insertAdjacentHTML('afterbegin',
    `<div class="fs-views" data-views><button type="button" data-view="a">${t.view.a}</button><button type="button" data-view="top" class="on">${t.view.top}</button><button type="button" data-view="b">${t.view.b}</button></div>`);
  const css = document.createElement('style');
  css.textContent =
    '.fs-views{position:absolute;left:50%;top:12px;transform:translateX(-50%);display:flex;gap:4px;padding:4px;border-radius:999px;background:color-mix(in srgb,var(--surface) 85%,transparent);border:1px solid var(--line,rgba(127,127,127,.25));z-index:2}' +
    '.fs-views button{min-width:64px;min-height:44px;padding:0 14px;border:0;border-radius:999px;background:none;color:var(--text);font:inherit;cursor:pointer}' +
    '.fs-views button.on{background:var(--accent,#7c6cf2);color:#fff}' +
    '.fs-split{display:flex;flex-wrap:wrap;gap:6px}.fs-split button{min-width:44px;min-height:44px;padding:0 10px;border-radius:10px;border:1px solid var(--line,rgba(127,127,127,.35));background:none;color:var(--text);font:600 16px/1 inherit;cursor:pointer}' +
    '.fs-split button.on{border-color:var(--accent,#7c6cf2);background:color-mix(in srgb,var(--accent,#7c6cf2) 18%,transparent)}.fs-split small{display:block;font-size:11px;opacity:.75;margin-top:2px}' +
    '.fs-sep{flex-basis:100%;height:0}' +
    '.fs-word{display:flex;flex-direction:column;gap:4px;margin-bottom:10px}.fs-word input{min-height:44px;font-size:18px;text-transform:uppercase}';
  root.append(css);
  const viewer = createStage(stage, lang).viewer;

  // ---------- панель ----------
  const unit0 = `<i>${t.mm}</i>`;
  const rng = (k: NumKey, label: string) => {
    const [min, max, step] = LIM[k];
    const unit = k === 'SC' || k === 'SW' || k === 'BW' ? '<i>%</i>' : unit0;
    return `<div class="rng" data-k="${k}"><span class="lbl">${label}</span><span class="rng-row">` +
      `<input type="range" min="${min}" max="${max}" step="${step}" value="${s[k]}" aria-label="${label}">` +
      `<span class="num"><input type="number" min="${min}" max="${max}" step="${step}" value="${s[k]}" inputmode="decimal" aria-label="${label}">${unit}</span></span>` +
      `<span class="minmax">${t.min} ${min} · ${t.max} ${max}</span></div>`;
  };
  const seg = (k: string, opts: [string, string][], cur: string) =>
    `<div class="seg wide" data-seg="${k}">${opts.map(([v, l]) => `<button type="button" data-v="${v}" class="${v === cur ? 'on' : ''}">${l}</button>`).join('')}</div>`;

  panel.innerHTML =
    `<details class="group" open><summary>${t.words}</summary>` +
      `<label class="fs-word"><span class="lbl">${t.a}</span><input type="text" data-word="a" value="${esc(s.a)}" maxlength="${MAXLEN}" autocomplete="off"></label>` +
      `<label class="fs-word"><span class="lbl">${t.b}</span><input type="text" data-word="b" value="${esc(s.b)}" maxlength="${MAXLEN}" autocomplete="off"></label>` +
      `<label class="field flat"><span class="lbl">${t.font}</span><select data-font>${FONTS.map(([id, n]) => `<option value="${id}"${id === s.font ? ' selected' : ''}>${n}</option>`).join('')}</select></label>` +
      rng('BW', t.BW) + `<p class="hint">${t.boldHint}</p>` +
      `<label class="switch"><input type="checkbox" data-keep${s.keep ? ' checked' : ''}><span>${t.keep}</span></label><p class="hint">${t.keepHint}</p>` +
      `<div data-split-g hidden><span class="lbl gap">${t.split}</span><div class="fs-split" data-split></div><p class="hint calc" data-split-hint></p></div></details>` +
    `<details class="group" open><summary>${t.size}</summary>${rng('W', t.W)}${rng('H', t.H)}${rng('G', t.G)}${rng('G2', t.G2)}${rng('WS', t.WS)}${rng('SK', t.SK)}<p class="hint">${t.skHint}</p>${rng('SC', t.SC)}<p class="hint">${t.scHint}</p></details>` +
    `<details class="group" open><summary>${t.base}</summary>` +
      seg('base', [['round', t.round], ['rect', t.rect], ['none', t.none]], s.base) +
      `<div data-base-opts>${rng('T', t.T)}${rng('M', t.M)}</div></details>` +
    `<details class="group" data-side-g><summary>${t.side}</summary>` +
      `<label class="fs-word"><span class="lbl">${t.front}</span><input type="text" data-side="front" value="${esc(s.front)}" maxlength="60" autocomplete="off"></label>` +
      `<label class="fs-word"><span class="lbl">${t.back}</span><input type="text" data-side="back" value="${esc(s.back)}" maxlength="60" autocomplete="off"></label>` +
      `<label class="field flat"><span class="lbl">${t.font}</span><select data-sfont>${(['sans', 'serif', 'hand', 'deco'] as const).map((g) =>
        `<optgroup label="${t.groups[g]}">${SIDE_FONTS.filter((f) => f[0] === g).map(([, id, n]) => `<option value="${id}"${id === s.sfont ? ' selected' : ''}>${n}</option>`).join('')}</optgroup>`).join('')}</select></label>` +
      seg('smode', [['raised', t.raised], ['engraved', t.engraved]], s.smode) + rng('SS', t.SS) + rng('SW', t.SW) + rng('SD', t.SDr) + '</details>';

  const $ = <E extends Element = HTMLElement>(q: string) => panel.querySelector<E>(q)!;

  panel.querySelectorAll<HTMLElement>('.rng').forEach((el) => {
    const k = el.dataset.k as NumKey;
    const [r, n] = el.querySelectorAll('input');
    const set = (raw: string) => {
      const x = raw.trim() === '' ? NaN : +raw;
      s[k] = Number.isFinite(x) ? clamp(x, LIM[k]) : s[k];
      r.value = n.value = String(s[k]); rebuild();
    };
    r.oninput = () => set(r.value);
    n.onchange = () => set(n.value);
  });
  panel.querySelectorAll<HTMLInputElement>('[data-word]').forEach((el) => {
    el.oninput = () => { s[el.dataset.word as 'a' | 'b'] = el.value; s.split = null; reframe = true; rebuild(); };
  });
  panel.querySelectorAll<HTMLInputElement>('[data-side]').forEach((el) => {
    el.oninput = () => { s[el.dataset.side as 'front' | 'back'] = el.value; rebuild(); };
  });
  $<HTMLSelectElement>('[data-sfont]').onchange = (e) => { s.sfont = (e.target as HTMLSelectElement).value; rebuild(); };
  $<HTMLSelectElement>('[data-font]').onchange = (e) => { s.font = (e.target as HTMLSelectElement).value; rebuild(); };
  $<HTMLInputElement>('[data-keep]').onchange = (e) => { s.keep = (e.target as HTMLInputElement).checked; rebuild(); };
  panel.querySelectorAll<HTMLElement>('[data-seg]').forEach((el) => {
    el.onclick = (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-v]');
      if (!b) return;
      el.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === b));
      (s as any)[el.dataset.seg!] = b.dataset.v;
      rebuild();
    };
  });
  // двойные буквы: нажатие добавляет букве ещё одну букву другого слова, забирая её у самой дальней из «многих»
  $('[data-split]').onclick = (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-i]');
    if (!b || !lastSplit) return;
    const g = +b.dataset.g!, i = +b.dataset.i!, sp = lastSplit[g].slice();
    if (sp[i] >= MAXK) return;
    let from = -1;
    for (let d = 1; d < sp.length && from < 0; d++) for (const j of [i - d, i + d]) if (from < 0 && j >= 0 && j < sp.length && sp[j] > 1) from = j;
    if (from < 0) return;
    sp[from]--; sp[i]++;
    s.split = lastSplit.map((r, j) => (j === g ? sp : r.slice())); rebuild();
  };
  let bed = bedPicker(panel, lang, (b) => { bed = b; viewer.setBed(b.x, b.y); rebuild(); });
  viewer.setBed(bed.x, bed.y);

  // ---------- виды ----------
  let view: 'a' | 'b' | 'top' = 'top';
  let turn = 0; // на сколько градусов модель развёрнута на столе
  let size: [number, number, number] = [100, 40, 45];
  const views = stage.querySelector<HTMLElement>('[data-views]')!;
  views.onclick = (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-view]');
    if (!b) return;
    view = b.dataset.view as typeof view;
    views.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === b));
    aim();
  };
  /** Камера: слева и справа — под 45° к линии блоков, почти по горизонтали; 3D — обычный ракурс. */
  function aim() {
    const [sx, sy, sz] = size;
    if (view === 'top') { viewer.frame(sx, sy, sz); return; }
    const cam = viewer.camera, fov = (cam.fov * Math.PI) / 180;
    const w = Math.max(sx, sy) * 0.8, d = Math.max(sz / 2 / Math.tan(fov / 2), w / 2 / (Math.tan(fov / 2) * cam.aspect)) * 2.4;
    // слайсер (x, y, z) → three (x, z, −y); взгляд слева — из (−1, −1) слайсера, справа — из (1, −1), с поворотом модели
    const sgn = view === 'a' ? -1 : 1, k = d / Math.SQRT2, cy = sz / 2, r = (turn * Math.PI) / 180;
    const x = sgn * k, y = -k, X = x * Math.cos(r) - y * Math.sin(r), Y = x * Math.sin(r) + y * Math.cos(r);
    cam.position.set(X, cy + d * 0.08, -Y);
    viewer.controls.target.set(0, cy, 0);
    viewer.controls.update();
  }

  // ---------- геометрия ----------
  const fontUrl = (id: string) => `/a/fonts/${id}.ttf`;
  const dims = stage.querySelector<HTMLElement>('[data-dims]')!;
  const fit = stage.querySelector<HTMLElement>('[data-fit]')!;
  let current: Promise<Model> | null = null;
  const setReady = (on: boolean) => root.querySelectorAll<HTMLButtonElement>('[data-download]').forEach((b) => { b.disabled = !on; });
  let seq = 0, timer = 0, reframe = true;
  let lastSplit: number[][] | null = null;

  function rebuild() {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      const my = ++seq;
      fit.className = 'fit'; fit.textContent = t.loading;
      const p = build({ ...s, split: s.split?.map((r) => r.slice()) ?? null });
      try { store?.save({ s }); } catch { /* сховище недоступне */ }
      current = p;
      p.then((m) => {
        if (my !== seq) return;
        $('[data-base-opts]').hidden = $('[data-side-g]').hidden = s.base === 'none';
        $('[data-k="SD"] .lbl').textContent = s.smode === 'raised' ? t.SDr : t.SDe;
        splitUi(m.short, m.split);
        if (!m.parts.length) { viewer.setParts([]); fit.className = 'fit bad'; fit.textContent = m.warn || t.empty; setReady(false); dims.textContent = ''; return; }
        viewer.setParts(m.view);
        size = m.size;
        const warn = fitsBed(bed, m.size[0], m.size[1], m.size[2]) ? m.warn : bedWarn(lang, bed);
        fit.className = warn ? 'fit bad' : 'fit';
        fit.textContent = warn || m.note || t.ok;
        turn = m.rot ?? 0;
        setReady(true);
        if (reframe) { aim(); reframe = false; }
        dims.innerHTML = m.size.map((v) => `<b>${+v.toFixed(1)}</b>`).join(' × ') + ` ${t.mm}`;
      }, (e) => {
        console.error(e);
        if (my === seq) { fit.className = 'fit bad'; fit.textContent = t.fontErr; }
      });
    }, 80);
  }

  /** Кнопки двойных букв: по парам слов — буквы короткого слова, под каждой — сколько букв длинного на ней. */
  function splitUi(short: string[][] | null, split: number[][] | null) {
    lastSplit = split;
    const el = $('[data-split-g]');
    const used = split ? split.map((r, g) => (r.some((x) => x > 1) ? g : -1)).filter((g) => g >= 0) : [];
    el.hidden = !short || !used.length;
    if (el.hidden) return;
    const extra = split!.flat().reduce((a, x) => a + x - 1, 0);
    // пары без двойных букв не показываем; между парами — разделитель
    $('[data-split]').innerHTML = used.map((g) => short![g].map((c, i) =>
      `<button type="button" data-g="${g}" data-i="${i}" class="${split![g][i] > 1 ? 'on' : ''}">${esc(c)}<small>×${split![g][i]}</small></button>`).join('')).join('<span class="fs-sep"></span>');
    $('[data-split-hint]').textContent = t.splitHint.replace('{n}', String(extra));
  }

  type Built = Model & { view: ViewPart[]; warn: string; note?: string; rot?: number; size: [number, number, number]; short: string[][] | null; split: number[][] | null };

  async function build(p: S): Promise<Built> {
    const none = (warn: string, short: string[][] | null = null, split: number[][] | null = null): Built =>
      ({ name: 'flip-sign', parts: [], view: [], warn, size: [0, 0, 0], short, split });
    const side = p.base !== 'none' && (p.front.trim() || p.back.trim());
    const [M, font, sfont] = await Promise.all([solid(), loadFont(fontUrl(p.font)), side ? loadFont(fontUrl(p.sfont)) : null]);
    const { CrossSection: CS, Manifold: MF } = M;
    const k = scope();
    try {
      const miss = new Set<string>();
      const ok = (c: string) => { if (font.charToGlyphIndex(c) > 0) return true; miss.add(c); return false; };
      let wa = words(p.a).map((w) => w.filter(ok)).filter((w) => w.length);
      let wb = words(p.b).map((w) => w.filter(ok)).filter((w) => w.length);
      const missText = miss.size ? t.noGlyph.replace('{c}', [...miss].join(' ')) : '';
      if (!wa.length || !wb.length) return none([t.empty, missText].filter(Boolean).join(' · '));
      // слов не поровну — пробелы не учитываем: одна пара из всех букв
      let spaceNote = '';
      if (wa.length !== wb.length) {
        if (wa.length > 1 || wb.length > 1) spaceNote = t.spaces.replace('{a}', String(wa.length)).replace('{b}', String(wb.length));
        wa = [wa.flat()]; wb = [wb.flat()];
      }
      const A = wa.flat(), B = wb.flat();
      type Pair = { aShort: boolean; short: string[]; long: string[]; split: number[] };
      const pairs: Pair[] = [];
      for (let g = 0; g < wa.length; g++) {
        const aShort = wa[g].length <= wb[g].length;
        const short = aShort ? wa[g] : wb[g], long = aShort ? wb[g] : wa[g];
        if (long.length > short.length * MAXK) return none(t.tooMany);
        const want = p.split?.[g];
        const split = p.split && p.split.length === wa.length && want && want.length === short.length && want.reduce((x, y) => x + y, 0) === long.length
          ? want : evenSplit(short.length, long.length);
        pairs.push({ aShort, short, long, split });
      }
      const shorts = pairs.map((q) => q.short), splits = pairs.map((q) => q.split);

      // Знаки над и под буквой (кратка Й, точки Ї и Ё, точка у «!», «?») — иначе в блоке висят отдельным кусочком.
      // Часть, целиком лежащую выше (ниже) самой большой, двигаем к ней по вертикали до касания и ещё на OVER внутрь, чтобы срослись.
      // Части рядом с телом (вторая половина Ы) не трогаем. Единицы — глиф с заглавной высотой 100.
      const STEP = 0.5, OVER = 3;
      const attach = (cs: CrossSection) => {
        const ps = cs.decompose().map(k);
        if (ps.length < 2) return cs;
        ps.sort((a, b) => b.area() - a.area());
        let body = ps[0];
        const bb = body.bounds();
        const rest: CrossSection[] = [];
        for (const q of ps.slice(1)) {
          const qb = q.bounds();
          const dir = qb.min[1] >= bb.max[1] - STEP ? -1 : qb.max[1] <= bb.min[1] + STEP ? 1 : 0;
          let d = dir < 0 ? qb.min[1] - bb.max[1] : bb.min[1] - qb.max[1];
          const far = dir < 0 ? qb.max[1] - bb.min[1] : bb.max[1] - qb.min[1]; // дальше — уже насквозь через букву
          let hit = false;
          if (dir) for (; d <= far; d += STEP) if (k(k(q.translate([0, dir * d])).intersect(body)).area() > 0) { hit = true; break; }
          if (hit) body = k(body.add(k(q.translate([0, dir * (d + OVER)]))));
          else rest.push(q);
        }
        return rest.length ? k(CS.union([body, ...rest])) : body;
      };

      // глиф буквы: контуры, растянутые (или с сохранёнными пропорциями) на высоту H, x — от центра
      const cache = new Map<string, { cs: CrossSection; w: number }>();
      const glyph = (c: string) => {
        let g = cache.get(c);
        if (g) return g;
        const sh = glyphShapes(font, c, 100)[0];
        if (!sh) return null;
        // «Жирность»: контур раздувается на BW% высоты глифа, потом буква всё равно вписывается в блок W×H
        let raw = attach(k(new CS(sh.contours, 'NonZero')));
        if (p.BW > 0) raw = k(raw.offset((sh.maxY - sh.minY) * p.BW / 100, 'Round', 2, 32));
        const bb = raw.bounds();
        const gw = bb.max[0] - bb.min[0], gh = bb.max[1] - bb.min[1];
        // І, Ї (и латинская I) не растягиваются никогда: растянутая палочка с точками читается как Т (решение пользователя 21.10.2026)
        const sy = p.H / gh, sx = p.keep || NARROW.has(c) ? sy : p.W / gw;
        const cs = k(k(raw.translate([-(bb.min[0] + bb.max[0]) / 2, -bb.min[1]])).scale([sx, sy]));
        g = { cs, w: gw * sx };
        cache.set(c, g);
        return g;
      };

      // оси: u — слева направо для взгляда слева (вдоль него идёт слово A), v — для взгляда справа (слово B)
      const r2 = Math.SQRT1_2;
      const U: [number, number, number] = [r2, -r2, 0], V: [number, number, number] = [r2, r2, 0];
      // zG — где буквы стоят (подставка или стол); низ букв опущен на SK: в подставку или под стол (там срезается)
      const zG = p.base === 'none' ? 0 : p.T, Z0 = zG - p.SK;
      const floor = p.base === 'none' && p.SK > 0 ? k(k(MF.cube([1e4, 1e4, 1e3])).translate([-5e3, -5e3, 0])) : null;
      // кусок держится, если заходит в подставку (или стоит на ней/на столе)
      const hold = p.SK > 0 && p.base !== 'none' ? zG - p.SK / 2 : zG + 0.3;
      /** Буква в плоскости (ось along, Z), выдавленная поперёк на len, центр — (cu, cv) в осях u, v. */
      const slab = (cs: CrossSection, along: 'u' | 'v', len: number, cu: number, cv: number) => {
        const ax = along === 'u' ? U : V, depth = along === 'u' ? [-V[0], -V[1], 0] : U; // ax × Z = depth (правая тройка)
        const c = [U[0] * cu + V[0] * cv, U[1] * cu + V[1] * cv, Z0];
        const m = [ax[0], ax[1], 0, 0, 0, 0, 1, 0, depth[0], depth[1], 0, 0, c[0], c[1], c[2], 1] as Parameters<Manifold['transform']>[0];
        return k(k(k(cs.extrude(len)).translate([0, 0, -len / 2])).transform(m));
      };

      // блоки: у каждого буква короткого слова и n букв длинного; размеры по u (взгляд слева) и v (справа)
      // first — первый блок слова: перед ним промежуток между словами
      type Block = { a: string[]; b: string[]; su: number; sv: number; label: string; first: boolean };
      const blocks: Block[] = [];
      const wid = (cs: string[]) => cs.reduce((x, c) => x + (glyph(c)?.w ?? 0), 0) + (cs.length - 1) * p.G2;
      for (const { aShort, short, long, split } of pairs) {
        let li = 0;
        for (let i = 0; i < short.length; i++) {
          const sh = [short[i]], lg = long.slice(li, li + split[i]);
          li += split[i];
          const a = aShort ? sh : lg, b = aShort ? lg : sh;
          blocks.push({ a, b, su: wid(a), sv: wid(b), label: `${a.join('')}·${b.join('')}`, first: i === 0 });
        }
      }
      // центры: по u и по v — подряд с зазором G (в каждом виде блоки стоят вплотную с этим зазором), между словами — ещё WS
      let pu = 0, pv = 0;
      const centers = blocks.map((bl, i) => {
        if (i && bl.first) { pu += p.WS; pv += p.WS; }
        const c = [pu + bl.su / 2, pv + bl.sv / 2]; pu += bl.su + p.G; pv += bl.sv + p.G; return c;
      });
      const ou = (pu - p.G) / 2, ov = (pv - p.G) / 2;

      const bodies: Manifold[] = [];
      const view: ViewPart[] = [];
      const loose: string[] = [];
      for (const [i, bl] of blocks.entries()) {
        const cu = centers[i][0] - ou, cv = centers[i][1] - ov;
        // буквы вдоль u (слово A) — каждая на своём месте по u, выдавлена на всю глубину блока по v
        const put = (cs: string[], along: 'u' | 'v', span: number, c0: number, other: number, depth: number) => {
          let x = c0 - span / 2;
          return cs.map((c) => {
            const g = glyph(c)!;
            const m = along === 'u' ? slab(g.cs, 'u', depth, x + g.w / 2, other) : slab(g.cs, 'v', depth, other, x + g.w / 2);
            x += g.w + p.G2;
            return m;
          });
        };
        const sa = put(bl.a, 'u', bl.su, cu, cv, bl.sv + 2);
        const sb = put(bl.b, 'v', bl.sv, cv, cu, bl.su + 2);
        const la = sa.length > 1 ? k(MF.union(sa)) : sa[0], lb = sb.length > 1 ? k(MF.union(sb)) : sb[0];
        let x = k(la.intersect(lb));
        if (floor) x = k(x.intersect(floor));
        if (x.isEmpty()) { loose.push(bl.label); continue; }
        // кусок, не стоящий на подставке (или на столе), не держится — показываем красным и предупреждаем
        const parts = x.decompose().map(k);
        const bad = parts.filter((q) => q.boundingBox().min[2] > hold || q.volume() < 1);
        if (bad.length) loose.push(bl.label);
        bodies.push(x);
        view.push({ geometry: toGeometry(x), role: 'detail', look: bad.length ? { color: '#E5484D' } : undefined });
      }
      if (!bodies.length) return none(t.empty, shorts, splits);
      const letters3 = k(MF.union(bodies));
      const bb = letters3.boundingBox();

      let base: Manifold | null = null, sideText: Manifold | null = null;
      const sideWarn: string[] = [];
      if (p.base !== 'none') {
        const w = bb.max[0] - bb.min[0] + 2 * p.M, h = bb.max[1] - bb.min[1] + 2 * p.M;
        const cx = (bb.min[0] + bb.max[0]) / 2, cy = (bb.min[1] + bb.max[1]) / 2;
        const R = p.base === 'round' ? Math.min(w, h) / 2 - 0.01 : 0;
        const plate = R > 0.05 ? k(k(CS.square([w - 2 * R, h - 2 * R], true)).offset(R, 'Round', 2, 96)) : k(CS.square([w, h], true));
        base = k(k(plate.extrude(p.T)).translate([cx, cy, 0]));
        // гнездо под утопленные буквы: части в 3MF не перекрываются
        if (p.SK > 0) base = k(base.subtract(letters3));
        // надписи на длинных сторонах: спереди (−Y) читается слева направо по +X, сзади (+Y) — по −X
        if (sfont) {
          const flat = w - 2 * R - 2; // ровная часть стороны (у скруглённой — между полукругами)
          const zc = p.T / 2, out: Manifold[] = [];
          for (const [txt, sgn] of [[p.front, -1], [p.back, 1]] as const) {
            const str = [...txt.normalize('NFC')].filter((c) => /\s/.test(c) || sfont.charToGlyphIndex(c) > 0).join('').trim();
            if (!str) continue;
            const sh = textContours(sfont, str, p.SS);
            if (!sh.contours.length) continue;
            const f = p.SW / 100, tw = (sh.maxX - sh.minX) * f;
            if (tw > flat) sideWarn.push(t.sideLong.replace('{w}', str));
            if (sh.maxY - sh.minY > p.T - 1) sideWarn.push(t.sideTall);
            let cs = k(k(new CS(sh.contours, 'NonZero')).translate([-(sh.minX + sh.maxX) / 2, -p.SS / 2]));
            if (f !== 1) cs = k(cs.scale([f, 1]));
            const raised = p.smode === 'raised';
            // локальные x → вдоль стороны (читается снаружи), y → вверх, z → наружу из подставки
            const ax = sgn < 0 ? [1, 0, 0] : [-1, 0, 0], az = [0, sgn, 0];
            const y0 = cy + (sgn * h) / 2;
            const m = [ax[0], ax[1], ax[2], 0, 0, 0, 1, 0, az[0], az[1], az[2], 0, cx, y0, zc, 1] as Parameters<Manifold['transform']>[0];
            const ex = raised ? k(k(cs.extrude(p.SD + 0.01)).translate([0, 0, -0.01])) : k(k(cs.extrude(p.SD)).translate([0, 0, -p.SD]));
            out.push(k(ex.transform(m)));
          }
          if (out.length) {
            sideText = out.length > 1 ? k(MF.union(out)) : out[0];
            // врезанная — гнездо в подставке, надпись его заполняет; выпуклая — гнездо на 0,01 мм, чтобы части не перекрывались
            base = k(base.subtract(sideText));
          }
        }
      }
      const parts: Model['parts'] = [];
      if (base) parts.push({ name: 'stand', role: 'base', geometry: toGeometry(base) });
      parts.push({ name: 'letters', role: 'detail', geometry: toGeometry(letters3) });
      if (sideText) { parts.push({ name: 'stand-text', role: 'detail', geometry: toGeometry(sideText) }); view.push({ geometry: toGeometry(sideText), role: 'detail' }); }
      if (base) view.unshift({ geometry: toGeometry(base), role: 'base' });
      let all = base ? k(MF.union([base, letters3, ...(sideText ? [sideText] : [])])) : letters3;
      if (p.SC !== 100) {
        const f = p.SC / 100;
        all = k(all.scale([f, f, f]));
        for (const q of parts) q.geometry.scale(f, f, f);
        for (const q of view) q.geometry.scale(f, f, f);
      }
      // вдоль стола не влезает — разворачиваем только на 45° (по просьбе пользователя); не влезает и так — остаётся прямо
      let rot = 0;
      const H3 = all.boundingBox().max[2];
      const fitAt = (b: { min: number[]; max: number[] }) => fitsBed(bed, b.max[0] - b.min[0], b.max[1] - b.min[1], H3);
      if (!fitAt(all.boundingBox()) && fitAt(k(k(k(all.project()).hull()).rotate(45)).bounds())) rot = 45;
      if (rot) {
        all = k(all.rotate([0, 0, rot]));
        const r = (rot * Math.PI) / 180;
        for (const q of parts) q.geometry.rotateZ(r);
        for (const q of view) q.geometry.rotateZ(r);
      }
      const ab = all.boundingBox();
      const warn = [loose.length ? t.loose.replace('{l}', loose.join(', ')) : '', ...new Set(sideWarn), spaceNote, missText].filter(Boolean).join(' · ');
      const name = `flip-${A.join('')}-${B.join('')}`;
      return {
        name, parts, single: toGeometry(all), view, warn, note: rot ? t.turned.replace('{d}', String(rot)) : '', rot, short: shorts, split: splits,
        size: [ab.max[0] - ab.min[0], ab.max[1] - ab.min[1], ab.max[2] - ab.min[2]],
      };
    } finally {
      k.free();
    }
  }

  rebuild();
  return { model: () => current ?? build(s) };
}
